import { useEffect, useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

// RFC 3492 Punycode. Pure client-side; no library dependency.
const PUNY_BASE = 36;
const PUNY_TMIN = 1;
const PUNY_TMAX = 26;
const PUNY_SKEW = 38;
const PUNY_DAMP = 700;
const PUNY_INITIAL_BIAS = 72;
const PUNY_INITIAL_N = 128;
const PUNY_DELIMITER = "-";

const punyAdapt = (delta, numPoints, firstTime) => {
  let d = firstTime ? Math.floor(delta / PUNY_DAMP) : delta >> 1;
  d += Math.floor(d / numPoints);
  let k = 0;
  while (d > ((PUNY_BASE - PUNY_TMIN) * PUNY_TMAX) >> 1) {
    d = Math.floor(d / (PUNY_BASE - PUNY_TMIN));
    k += PUNY_BASE;
  }
  return k + Math.floor(((PUNY_BASE - PUNY_TMIN + 1) * d) / (d + PUNY_SKEW));
};

const digitToBasic = (digit) => (digit < 26 ? digit + 97 : digit + 22);

const basicToDigit = (cp) => {
  if (cp - 48 < 10) return cp - 22;
  if (cp - 65 < 26) return cp - 65;
  if (cp - 97 < 26) return cp - 97;
  return PUNY_BASE;
};

const punyEncodeLabel = (input) => {
  const codePoints = Array.from(input, (c) => c.codePointAt(0));
  const output = [];
  let n = PUNY_INITIAL_N;
  let delta = 0;
  let bias = PUNY_INITIAL_BIAS;
  for (const cp of codePoints) {
    if (cp < 0x80) output.push(cp);
  }
  const basicLength = output.length;
  let handled = basicLength;
  if (basicLength) output.push(PUNY_DELIMITER.charCodeAt(0));
  const total = codePoints.length;
  while (handled < total) {
    let m = Infinity;
    for (const cp of codePoints) if (cp >= n && cp < m) m = cp;
    if (m - n > Math.floor((Number.MAX_SAFE_INTEGER - delta) / (handled + 1))) {
      throw new RangeError("overflow");
    }
    delta += (m - n) * (handled + 1);
    n = m;
    for (const cp of codePoints) {
      if (cp < n) {
        delta += 1;
        if (delta > Number.MAX_SAFE_INTEGER) throw new RangeError("overflow");
      }
      if (cp === n) {
        let q = delta;
        for (let k = PUNY_BASE; ; k += PUNY_BASE) {
          const t =
            k <= bias
              ? PUNY_TMIN
              : k >= bias + PUNY_TMAX
              ? PUNY_TMAX
              : k - bias;
          if (q < t) break;
          output.push(digitToBasic(t + ((q - t) % (PUNY_BASE - t))));
          q = Math.floor((q - t) / (PUNY_BASE - t));
        }
        output.push(digitToBasic(q));
        bias = punyAdapt(delta, handled + 1, handled === basicLength);
        delta = 0;
        handled += 1;
      }
    }
    delta += 1;
    n += 1;
  }
  return String.fromCharCode(...output);
};

const punyDecodeLabel = (input) => {
  const codePoints = [];
  const inputLen = input.length;
  let i = 0;
  let n = PUNY_INITIAL_N;
  let bias = PUNY_INITIAL_BIAS;
  let basic = input.lastIndexOf(PUNY_DELIMITER);
  if (basic < 0) basic = 0;
  for (let j = 0; j < basic; j += 1) {
    const cp = input.charCodeAt(j);
    if (cp >= 0x80) throw new RangeError("not-basic");
    codePoints.push(cp);
  }
  let index = basic > 0 ? basic + 1 : 0;
  while (index < inputLen) {
    const oldi = i;
    for (let w = 1, k = PUNY_BASE; ; k += PUNY_BASE) {
      if (index >= inputLen) throw new RangeError("invalid-input");
      const digit = basicToDigit(input.charCodeAt(index++));
      if (
        digit >= PUNY_BASE ||
        digit > Math.floor((Number.MAX_SAFE_INTEGER - i) / w)
      ) {
        throw new RangeError("overflow");
      }
      i += digit * w;
      const t =
        k <= bias
          ? PUNY_TMIN
          : k >= bias + PUNY_TMAX
          ? PUNY_TMAX
          : k - bias;
      if (digit < t) break;
      const baseMinusT = PUNY_BASE - t;
      if (w > Math.floor(Number.MAX_SAFE_INTEGER / baseMinusT)) {
        throw new RangeError("overflow");
      }
      w *= baseMinusT;
    }
    const out = codePoints.length + 1;
    bias = punyAdapt(i - oldi, out, oldi === 0);
    if (Math.floor(i / out) > Number.MAX_SAFE_INTEGER - n) {
      throw new RangeError("overflow");
    }
    n += Math.floor(i / out);
    i %= out;
    codePoints.splice(i++, 0, n);
  }
  return String.fromCodePoint(...codePoints);
};

const isAllAscii = (s) => /^[\x00-\x7F]*$/.test(s);

// Convert a dotted domain to its ASCII (punycode) form.
// Each label is encoded independently; already-ASCII labels pass through.
const domainToAscii = (domain) => {
  if (!domain) return "";
  return domain
    .toLowerCase()
    .split(".")
    .map((label) => {
      if (!label) return label;
      if (isAllAscii(label)) return label;
      return `xn--${punyEncodeLabel(label)}`;
    })
    .join(".");
};

// Convert a dotted domain to its Unicode form.
// Labels prefixed with xn-- are decoded; others pass through unchanged.
const domainToUnicode = (domain) => {
  if (!domain) return "";
  return domain
    .split(".")
    .map((label) => {
      const lower = label.toLowerCase();
      if (!lower.startsWith("xn--")) return label;
      try {
        return punyDecodeLabel(lower.slice(4));
      } catch {
        return label;
      }
    })
    .join(".");
};

// Try to pull a hostname out of a URL. If the input isn't a URL, treat the
// whole string as a bare domain. Returned shape makes it easy to reconstruct
// the URL after converting the hostname.
const splitUrl = (raw) => {
  const trimmed = raw.trim();
  if (!trimmed) return { kind: "empty" };
  try {
    const u = new URL(trimmed);
    return {
      kind: "url",
      protocol: u.protocol,
      username: u.username,
      password: u.password,
      hostname: u.hostname,
      port: u.port,
      pathname: u.pathname,
      search: u.search,
      hash: u.hash,
    };
  } catch {
    return { kind: "domain", hostname: trimmed };
  }
};

const rebuildUrl = (parts, newHostname) => {
  if (parts.kind !== "url") return newHostname;
  const auth =
    parts.username || parts.password
      ? `${parts.username}${parts.password ? `:${parts.password}` : ""}@`
      : "";
  const port = parts.port ? `:${parts.port}` : "";
  return `${parts.protocol}//${auth}${newHostname}${port}${parts.pathname}${parts.search}${parts.hash}`;
};

const describeLabels = (domain) => {
  if (!domain) return [];
  return domain.split(".").map((label) => {
    const lower = label.toLowerCase();
    const isPuny = lower.startsWith("xn--");
    const isUnicode = !isAllAscii(label);
    let kind = "ascii";
    if (isPuny) kind = "puny";
    else if (isUnicode) kind = "unicode";
    return { label, kind };
  });
};

const EXAMPLE_INPUT = "https://münchen.example.de/weißbier?q=prost";

const PunycodeConverter = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState(EXAMPLE_INPUT);

  const result = useMemo(() => {
    const parts = splitUrl(input);
    if (parts.kind === "empty") return { empty: true };
    const hostname = parts.hostname;
    if (!hostname) {
      return { error: "no_host", kind: parts.kind };
    }
    let ascii;
    let unicode;
    try {
      ascii = domainToAscii(hostname);
    } catch {
      return { error: "encode_failed", kind: parts.kind };
    }
    try {
      unicode = domainToUnicode(hostname);
    } catch {
      return { error: "decode_failed", kind: parts.kind };
    }
    return {
      kind: parts.kind,
      parts,
      hostname,
      ascii,
      unicode,
      asciiLabels: describeLabels(ascii),
      unicodeLabels: describeLabels(unicode),
      asciiFull: rebuildUrl(parts, ascii),
      unicodeFull: rebuildUrl(parts, unicode),
      hasConversion: ascii !== unicode,
    };
  }, [input]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setInput("");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const copy = async (text, label) => {
    if (!text) {
      toast.error(t("punycode_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const renderLabels = (labels) => (
    <div className="flex flex-wrap gap-1.5 mt-2" aria-hidden="true">
      {labels.map((lbl, idx) => {
        const base =
          "inline-flex items-center px-2 py-0.5 rounded-md text-xs font-mono border";
        const styles =
          lbl.kind === "puny"
            ? "bg-amber-50 border-amber-200 text-amber-800"
            : lbl.kind === "unicode"
            ? "bg-emerald-50 border-emerald-200 text-emerald-800"
            : "bg-gray-50 border-gray-200 text-gray-700";
        return (
          <span key={`${idx}-${lbl.label}`} className={`${base} ${styles}`}>
            {lbl.label || <em className="opacity-60">∅</em>}
          </span>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("punycode_title")}
            </h1>
            <p className="text-gray-600">{t("punycode_intro")}</p>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="punycode-input"
                className="text-sm font-medium text-gray-700"
              >
                {t("punycode_input_label")}
              </label>
              <textarea
                id="punycode-input"
                rows={3}
                spellCheck={false}
                autoComplete="off"
                autoCapitalize="off"
                className="w-full p-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                placeholder={t("punycode_input_placeholder")}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                aria-invalid={result?.error ? "true" : "false"}
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <StandardButton
                onClick={() => setInput(EXAMPLE_INPUT)}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("punycode_load_example")}
              </StandardButton>
              <StandardButton
                onClick={() => setInput("")}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("punycode_clear")}
              </StandardButton>
              <StandardButton
                onClick={async () => {
                  try {
                    const text = await navigator.clipboard.readText();
                    setInput(text);
                  } catch {
                    toast.error(t("failed_to_copy"));
                  }
                }}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("punycode_paste")}
              </StandardButton>
            </div>

            {result?.empty && (
              <div className="text-sm text-gray-500">
                {t("punycode_empty_hint")}
              </div>
            )}

            {result?.error && (
              <div
                role="alert"
                className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md"
              >
                {t(
                  result.error === "no_host"
                    ? "punycode_error_no_host"
                    : result.error === "encode_failed"
                    ? "punycode_error_encode_failed"
                    : "punycode_error_decode_failed"
                )}
              </div>
            )}

            {result && !result.empty && !result.error && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                    <div className="flex items-center justify-between mb-1">
                      <strong className="text-indigo-700 text-sm">
                        {t("punycode_unicode_label")}
                      </strong>
                      <StandardButton
                        onClick={() =>
                          copy(result.unicode, t("punycode_unicode_label"))
                        }
                        type="secondary"
                        variant="outlined"
                        size="small"
                      >
                        {t("copy_button")}
                      </StandardButton>
                    </div>
                    <p className="font-mono text-sm break-all text-gray-800">
                      {result.unicode}
                    </p>
                    {renderLabels(result.unicodeLabels)}
                    <p className="mt-2 text-xs text-gray-500">
                      {t("punycode_unicode_hint")}
                    </p>
                  </div>

                  <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                    <div className="flex items-center justify-between mb-1">
                      <strong className="text-indigo-700 text-sm">
                        {t("punycode_ascii_label")}
                      </strong>
                      <StandardButton
                        onClick={() =>
                          copy(result.ascii, t("punycode_ascii_label"))
                        }
                        type="secondary"
                        variant="outlined"
                        size="small"
                      >
                        {t("copy_button")}
                      </StandardButton>
                    </div>
                    <p className="font-mono text-sm break-all text-gray-800">
                      {result.ascii}
                    </p>
                    {renderLabels(result.asciiLabels)}
                    <p className="mt-2 text-xs text-gray-500">
                      {t("punycode_ascii_hint")}
                    </p>
                  </div>
                </div>

                {result.kind === "url" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="bg-white p-4 rounded-md border border-gray-200">
                      <div className="flex items-center justify-between mb-1">
                        <strong className="text-gray-700 text-sm">
                          {t("punycode_unicode_url_label")}
                        </strong>
                        <StandardButton
                          onClick={() =>
                            copy(
                              result.unicodeFull,
                              t("punycode_unicode_url_label")
                            )
                          }
                          type="secondary"
                          variant="outlined"
                          size="small"
                        >
                          {t("copy_button")}
                        </StandardButton>
                      </div>
                      <p className="font-mono text-sm break-all text-gray-800">
                        {result.unicodeFull}
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded-md border border-gray-200">
                      <div className="flex items-center justify-between mb-1">
                        <strong className="text-gray-700 text-sm">
                          {t("punycode_ascii_url_label")}
                        </strong>
                        <StandardButton
                          onClick={() =>
                            copy(
                              result.asciiFull,
                              t("punycode_ascii_url_label")
                            )
                          }
                          type="secondary"
                          variant="outlined"
                          size="small"
                        >
                          {t("copy_button")}
                        </StandardButton>
                      </div>
                      <p className="font-mono text-sm break-all text-gray-800">
                        {result.asciiFull}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-300" />
                    {t("punycode_legend_unicode")}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm bg-amber-100 border border-amber-300" />
                    {t("punycode_legend_puny")}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm bg-gray-100 border border-gray-300" />
                    {t("punycode_legend_ascii")}
                  </span>
                </div>
              </>
            )}

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("punycode_note_spec")}</p>
              <p>{t("punycode_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default PunycodeConverter;
